import type { Pt, Ring, Poly } from './types'

export interface BBox {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

export function signedArea(ring: Ring): number {
  let s = 0
  for (let i = 0, n = ring.length; i < n; i++) {
    const [x1, y1] = ring[i]
    const [x2, y2] = ring[(i + 1) % n]
    s += x1 * y2 - x2 * y1
  }
  return s / 2
}

// CCW means positive signed area in a y-up frame. The app works in mm with
// y increasing downward on screen, but winding conventions here are purely
// algebraic: "CCW" = positive signed area.
export function ensureWinding(ring: Ring, ccw: boolean): Ring {
  const a = signedArea(ring)
  if ((a >= 0) === ccw) return ring
  return ring.slice().reverse()
}

export function ringBBox(ring: Ring): BBox {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const [x, y] of ring) {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

export function polyBBox(poly: Poly): BBox {
  return ringBBox(poly[0])
}

export function bboxOverlap(a: BBox, b: BBox): boolean {
  return a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY
}

export function pointInRing(p: Pt, ring: Ring): boolean {
  // Even-odd rule.
  let inside = false
  const [px, py] = p
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersects = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    if (intersects) inside = !inside
  }
  return inside
}

export function distToSegment(p: Vec, a: Vec, b: Vec): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

interface Vec {
  x: number
  y: number
}

// Number of segments used to tessellate a full circle of radius r at chord
// tolerance tol. Shared by preview and every exporter so they always agree.
export function circleSegments(r: number, tol: number): number {
  const t = Math.min(tol, r * 0.5)
  const n = Math.ceil(Math.PI / Math.acos(1 - t / r))
  return Math.max(12, Math.min(512, n))
}

export function circleRing(cx: number, cy: number, r: number, tol: number): Ring {
  const n = circleSegments(r, tol)
  const ring: Ring = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2
    ring.push([cx + r * Math.cos(a), cy + r * Math.sin(a)])
  }
  return ring // positive signed area (CCW in the algebraic sense)
}

export function translateRing(ring: Ring, dx: number, dy: number): Ring {
  return ring.map(([x, y]) => [x + dx, y + dy])
}

export function rotateRing(ring: Ring, angleRad: number): Ring {
  if (angleRad === 0) return ring
  const c = Math.cos(angleRad)
  const s = Math.sin(angleRad)
  return ring.map(([x, y]) => [x * c - y * s, x * s + y * c])
}

// Inset a convex CCW ring by distance d using successive half-plane clips.
// Returns null when the ring collapses. Only valid for convex rings; the
// pipeline guarantees convexity where this is used (Voronoi cells clipped to
// a convex boundary).
export function convexInset(ring: Ring, d: number): Ring | null {
  if (d <= 0) return ring
  let poly = ring
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[i]
    const [bx, by] = ring[(i + 1) % n]
    const ex = bx - ax
    const ey = by - ay
    const len = Math.hypot(ex, ey)
    if (len < 1e-12) continue
    // Inward normal for a CCW ring is the left of travel.
    const nx = -ey / len
    const ny = ex / len
    const ox = ax + nx * d
    const oy = ay + ny * d
    poly = clipHalfPlane(poly, ox, oy, nx, ny)
    if (poly.length < 3) return null
  }
  if (Math.abs(signedArea(poly)) < 1e-6) return null
  return poly
}

// Keep the part of `ring` on the side of the line through (ox, oy) with
// normal (nx, ny) that the normal points into.
function clipHalfPlane(ring: Ring, ox: number, oy: number, nx: number, ny: number): Ring {
  const out: Ring = []
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[i]
    const [bx, by] = ring[(i + 1) % n]
    const da = (ax - ox) * nx + (ay - oy) * ny
    const db = (bx - ox) * nx + (by - oy) * ny
    if (da >= 0) out.push([ax, ay])
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db)
      out.push([ax + (bx - ax) * t, ay + (by - ay) * t])
    }
  }
  return out
}

// General corner rounding for a CCW simple ring: fillets convex corners
// (removing area) and reflex corners (adding area) alike. Radius clamps per
// corner at the edge midpoints; r <= 0 returns the ring unchanged.
export function roundCorners(ring: Ring, r: number, tol: number): Ring {
  if (r <= 0 || ring.length < 3) return ring
  const n = ring.length
  const out: Ring = []
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[(i - 1 + n) % n]
    const [bx, by] = ring[i]
    const [cx, cy] = ring[(i + 1) % n]
    const l1 = Math.hypot(bx - ax, by - ay)
    const l2 = Math.hypot(cx - bx, cy - by)
    if (l1 < 1e-9 || l2 < 1e-9) {
      out.push([bx, by])
      continue
    }
    const e1x = (bx - ax) / l1
    const e1y = (by - ay) / l1
    const e2x = (cx - bx) / l2
    const e2y = (cy - by) / l2
    const dot = Math.max(-1, Math.min(1, e1x * e2x + e1y * e2y))
    const cross = e1x * e2y - e1y * e2x
    const turn = Math.acos(dot)
    if (turn < 1e-3 || Math.abs(cross) < 1e-9) {
      out.push([bx, by])
      continue
    }
    const s = Math.sign(cross) // +1 convex (left turn on CCW), -1 reflex
    let t = r * Math.tan(turn / 2)
    const tMax = Math.min(l1, l2) / 2
    let rr = r
    if (t > tMax) {
      t = tMax
      rr = t / Math.tan(turn / 2)
    }
    if (rr < 1e-6) {
      out.push([bx, by])
      continue
    }
    const p1x = bx - e1x * t
    const p1y = by - e1y * t
    // Center sits along the left normal for convex corners, the right normal
    // for reflex ones; the sweep direction flips with it.
    const ox = p1x - e1y * rr * s
    const oy = p1y + e1x * rr * s
    const a1 = Math.atan2(p1y - oy, p1x - ox)
    const steps = Math.max(2, Math.ceil(turn / (2 * Math.acos(Math.max(0.1, 1 - Math.min(tol, rr * 0.5) / rr)))))
    for (let k = 0; k <= steps; k++) {
      const a = a1 + (s * turn * k) / steps
      out.push([ox + rr * Math.cos(a), oy + rr * Math.sin(a)])
    }
  }
  return dedupeRing(out, ring)
}

function dedupeRing(out: Ring, fallback: Ring): Ring {
  const eps = 1e-4
  const dedup: Ring = []
  for (const p of out) {
    const prev = dedup[dedup.length - 1]
    if (prev && Math.hypot(p[0] - prev[0], p[1] - prev[1]) < eps) continue
    dedup.push(p)
  }
  while (
    dedup.length > 1 &&
    Math.hypot(
      dedup[0][0] - dedup[dedup.length - 1][0],
      dedup[0][1] - dedup[dedup.length - 1][1],
    ) < eps
  ) {
    dedup.pop()
  }
  return dedup.length >= 3 ? dedup : fallback
}

// Round the corners of a convex CCW ring with arcs of radius r, sampled at
// chord tolerance tol. The radius is clamped per corner so tangent points
// never pass the edge midpoints; r <= 0 returns the ring unchanged.
export function roundConvexCorners(ring: Ring, r: number, tol: number): Ring {
  if (r <= 0 || ring.length < 3) return ring
  const n = ring.length
  const out: Ring = []
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[(i - 1 + n) % n]
    const [bx, by] = ring[i]
    const [cx, cy] = ring[(i + 1) % n]
    const l1 = Math.hypot(bx - ax, by - ay)
    const l2 = Math.hypot(cx - bx, cy - by)
    if (l1 < 1e-9 || l2 < 1e-9) {
      out.push([bx, by])
      continue
    }
    const e1x = (bx - ax) / l1
    const e1y = (by - ay) / l1
    const e2x = (cx - bx) / l2
    const e2y = (cy - by) / l2
    const dot = Math.max(-1, Math.min(1, e1x * e2x + e1y * e2y))
    const turn = Math.acos(dot) // exterior angle, 0..pi
    if (turn < 1e-3) {
      out.push([bx, by]) // nearly straight: no arc needed
      continue
    }
    // Trim distance along each edge for radius r, clamped to half-edges.
    let t = r * Math.tan(turn / 2)
    const tMax = Math.min(l1, l2) / 2
    let rr = r
    if (t > tMax) {
      t = tMax
      rr = t / Math.tan(turn / 2)
    }
    if (rr < 1e-6) {
      out.push([bx, by])
      continue
    }
    const p1x = bx - e1x * t
    const p1y = by - e1y * t
    // Arc center: from the first tangent point along the interior normal of
    // the incoming edge (left of travel on a CCW ring).
    const ox = p1x - e1y * rr
    const oy = p1y + e1x * rr
    const a1 = Math.atan2(p1y - oy, p1x - ox)
    // CCW travel with the center on the interior (left) side sweeps the arc
    // angle upward by the exterior angle. (Checked on a CCW square: the
    // corner at (10,0) runs from -pi/2 to 0.)
    const steps = Math.max(2, Math.ceil(turn / (2 * Math.acos(Math.max(0.1, 1 - Math.min(tol, rr * 0.5) / rr)))))
    for (let s = 0; s <= steps; s++) {
      const a = a1 + (turn * s) / steps
      out.push([ox + rr * Math.cos(a), oy + rr * Math.sin(a)])
    }
  }
  // Clamped tangent points of adjacent corners can coincide at the edge
  // midpoint; drop near-duplicate consecutive vertices (wrap included) so
  // downstream triangulation and welding never see zero-length edges.
  const eps = 1e-4
  const dedup: Ring = []
  for (const p of out) {
    const prev = dedup[dedup.length - 1]
    if (prev && Math.hypot(p[0] - prev[0], p[1] - prev[1]) < eps) continue
    dedup.push(p)
  }
  while (
    dedup.length > 1 &&
    Math.hypot(
      dedup[0][0] - dedup[dedup.length - 1][0],
      dedup[0][1] - dedup[dedup.length - 1][1],
    ) < eps
  ) {
    dedup.pop()
  }
  return dedup.length >= 3 ? dedup : ring
}

export function centroid(ring: Ring): Pt {
  let cx = 0
  let cy = 0
  let area = 0
  for (let i = 0, n = ring.length; i < n; i++) {
    const [x1, y1] = ring[i]
    const [x2, y2] = ring[(i + 1) % n]
    const f = x1 * y2 - x2 * y1
    area += f
    cx += (x1 + x2) * f
    cy += (y1 + y2) * f
  }
  if (Math.abs(area) < 1e-12) {
    const b = ringBBox(ring)
    return [(b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2]
  }
  area *= 3
  return [cx / area, cy / area]
}

// Does a tessellated ring still describe the given analytic circle?
export function ringMatchesCircle(ring: Ring, c: { cx: number; cy: number; r: number }, eps: number): boolean {
  for (const [x, y] of ring) {
    const d = Math.hypot(x - c.cx, y - c.cy)
    if (Math.abs(d - c.r) > eps) return false
  }
  return true
}
